/**
 * localStorage shim for `default_portal_id`.
 *
 * Per `auth-admin-20260522/AUTH_FLOW.md §9`: the backend should
 * surface `default_portal_id` on `/auth/me` (and accept
 * `PATCH /me { default_portal_id }`). Until that ships, the FE
 * persists the user's last-used portal locally so the chooser can
 * still surface the "Last used" badge across sessions on the same
 * device.
 *
 * Note this is a per-device shim — it does not roam. The backend
 * ticket is filed as a soft-suggestion; once `/auth/me` returns
 * `default_portal_id`, the chooser preferentially reads from
 * session state and only falls back to this shim for first-paint
 * before the session resolves.
 *
 * Storage key is namespaced (`sparta.auth.defaultPortalId`) to keep
 * the localStorage flat. Read/write are wrapped in try/catch — in
 * private-mode Safari and some corporate-blocked browsers
 * localStorage throws on access; we degrade silently.
 */

import { isPortalId, type PortalId } from './portal-id.js';

const STORAGE_KEY = 'sparta.auth.defaultPortalId';

/**
 * Read the persisted default portal. Returns `null` if no value is
 * stored, if the stored value is corrupted, or if localStorage is
 * unavailable.
 */
export function readDefaultPortalId(): PortalId | null {
  try {
    if (typeof window === 'undefined') return null;
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return isPortalId(raw) ? raw : null;
  } catch {
    // localStorage access may throw under private-mode Safari and
    // some corporate browser policies. Caller treats null as "no
    // preference recorded".
    return null;
  }
}

/**
 * Persist the user's chosen portal as their default. Silently
 * no-ops if localStorage is unavailable.
 */
export function writeDefaultPortalId(id: PortalId): void {
  try {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(STORAGE_KEY, id);
  } catch {
    /* see readDefaultPortalId comment */
  }
}

/**
 * Clear the persisted default portal. Called on sign-out so the
 * next user on the same device starts with no "Last used" hint.
 */
export function clearDefaultPortalId(): void {
  try {
    if (typeof window === 'undefined') return;
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* see readDefaultPortalId comment */
  }
}
